import React, { useState, useRef } from "react";
import { Container, Form, Spinner } from "react-bootstrap";
import { useLocation, useNavigate } from "react-router-dom";
import { Helmet, HelmetProvider } from "react-helmet-async";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import BackButton from "../../../components/UI_Kit/BackButton/BackButton";
import PhotoMetadataModal from '../../../components/Modals/PhotoMetadataModal';

import EXIF from "exif-js";
// Import form components
import TypeOneForm from "../Create/Step2/forms/TypeOneForm";
import TypeTwoForm from "../Create/Step2/forms/TypeTwoForm";
import TypeThreeForm from "../Create/Step2/forms/TypeThreeForm";
import TypeFourForm from "../Create/Step2/forms/TypeFourForm";
import CameraConfig from "./EditCameraConfig";


import { deleteCameraApi, savePointDataApi } from './editPointApi';
import { useEditPointForm } from '../../../hooks/useEditPointForm';
import { usePointConfigurations } from '../../../hooks/usePointConfigurations'; // Import the new hook

import { convertHeic } from "../../../utils/photoUtils";

import { useGeolocated } from "react-geolocated";

const EditPoint = ({ pointToEdit: propPointToEdit, quotationId: propQuotationId, onClose }) => {

  const {
    coords,
    isGeolocationAvailable,
    isGeolocationEnabled,
    positionError,
  } = useGeolocated({
    positionOptions: {
      enableHighAccuracy: true,
    },
    userDecisionTimeout: 5000,
  });

  const location = useLocation();
  const navigate = useNavigate();
  const pointToEdit = propPointToEdit || location.state?.pointToEdit;
  const quotationId = propQuotationId || location.state?.quotationId;

  const isModal = !!propPointToEdit || !!propQuotationId; // Determine if opened as a modal

  // Use the custom hook for form state management
  const {
    formData,
    setFormData,
    initialCameraIds,
    setInitialCameraIds,
    handleChange
  } = useEditPointForm(pointToEdit, navigate);

  // Use the custom hook for fetching configurations
  const { pointConfigurations, isLoadingConfig, configError } = usePointConfigurations();

  const [saving, setSaving] = useState(false);
  const [showMenu, setShowMenu] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [selectedPhotoIndex, setSelectedPhotoIndex] = useState(null);
  const [editedMetadata, setEditedMetadata] = useState({ name: "", location: "" });
  const fileInputRef = useRef(null);
  const [showCameraConfig, setShowCameraConfig] = useState(false);
  const [isUploading, setIsUploading] = useState(false); // New state for upload loading

  // Define required fields for each point type
  const requiredFields = {
    vision_point: [
      { name: "pointName", message: "Le nom du point de vision est requis." },
      { name: "photos", message: "Au moins une photo est requise pour le point de vision." },
      { name: "supportType", message: "Le type de support est requis." },
      { name: "installationType", message: "Le type d'installation est requis." },
      { name: "powerSupplyType", message: "Le type d'alimentation est requis." },
    ],
    relay_point: [
      { name: "pointName", message: "Le nom du point relais est requis." },
      // { name: "photos", message: "Au moins une photo est requise pour le point relais." },
      { name: "supportType", message: "Le type de support est requis." },
      { name: "installationType", message: "Le type d'installation est requis." },
      { name: "powerSupplyType", message: "Le type d'alimentation est requis." },
      { name: "hasCameras", message: "Veuillez indiquer si le point relais a des caméras." },
    ],
    high_point: [
      { name: "pointName", message: "Le nom du point haut est requis." },
      { name: "photos", message: "Au moins une photo est requise pour le point haut." },
      { name: "supportType", message: "Le type de support est requis." },
      { name: "installationType", message: "Le type d'installation est requis." },
      { name: "powerSupplyType", message: "Le type d'alimentation est requis." },
      { name: "coverageArea", message: "La zone de couverture est requise." },
    ],
    surveillance_center: [
      { name: "pointName", message: "Le nom du centre de surveillance est requis." },
      { name: "photos", message: "Au moins une photo est requise pour le centre de surveillance." },
      { name: "supportType", message: "Le type de support est requis." },
      { name: "installationType", message: "Le type d'installation est requis." },
      { name: "powerSupplyType", message: "Le type d'alimentation est requis." },
      { name: "recorderSize", message: "La taille de l'enregistreur est requise." },
      { name: "screenSize", message: "La taille de l'écran est requise." },
    ],
  };

  // Validation function
  const validateForm = (currentPointType, data) => {
    if (!currentPointType) {
      toast.error("Veuillez sélectionner un type de point.");
      return false;
    }

    const fieldsToValidate = requiredFields[currentPointType];
    if (!fieldsToValidate) {
      console.warn(`No validation rules defined for point type: ${currentPointType}`);
      return true; // No specific validation rules, so consider it valid
    }

    for (const field of fieldsToValidate) {
      // Special handling for photos array
      if (field.name === "photos") {
        if (!data.photos || data.photos.length === 0) {
          toast.error(field.message);
          return false;
        }
      } else if (!data[field.name] || String(data[field.name]).trim().length === 0) {
        toast.error(field.message);
        return false;
      }
    }
    return true;
  };

  const convertDMSToDD = (degrees, minutes, seconds, direction) => {
    let dd = degrees + minutes / 60 + seconds / 3600;
    if (direction === "S" || direction === "W") {
      dd = -dd;
    }
    return dd;
  };

  const getPhotoLocation = (file) => {
    return new Promise((resolve) => {
      // Helper to resolve with browser location
      const getBrowserLocation = () => {
        return new Promise(async (geoResolve) => {
          if (isGeolocationAvailable && isGeolocationEnabled) {
            if (coords) {
              geoResolve({
                latitude: coords.latitude,
                longitude: coords.longitude,
              });
            } else if (positionError) {
              geoResolve({ latitude: null, longitude: null });
            }
          } else {
            geoResolve({ latitude: null, longitude: null }); // Geolocation not supported
          }
        });
      };

      // --- Handle HEIC files ---
      if (file.name && file.name.toLowerCase().endsWith(".heic")) {
        const reader = new FileReader();
        reader.onload = async function (e) {
          try {
            const ExifReader = await import("exifreader");
            const tags = ExifReader.default.load(e.target.result);

            if (tags.GPSLatitude && tags.GPSLongitude) {
              const latRef = tags.GPSLatitudeRef
                ? tags.GPSLatitudeRef.value[0]
                : "N";
              const lonRef = tags.GPSLongitudeRef
                ? tags.GPSLongitudeRef.value[0]
                : "E";

              const latitude = tags.GPSLatitude.description;
              const longitude = tags.GPSLongitude.description;

              file.exifData = {
                latitude,
                longitude,
                latitudeRef: latRef,
                longitudeRef: lonRef,
              };

              resolve({ latitude, longitude });
            } else {
              // No GPS data in HEIC -> fallback
              const browserLocation = await getBrowserLocation(); // Await the promise
              resolve(browserLocation);
            }
          } catch (error) {
            console.error("Error extracting HEIC metadata:", error);
            const browserLocation = await getBrowserLocation(); // Await the promise
            resolve(browserLocation);
          }
        };
        reader.readAsArrayBuffer(file);

        // --- Handle JPEG/TIFF and others ---
      } else {
        EXIF.getData(file, async function () {
          const lat = EXIF.getTag(this, "GPSLatitude");
          const lon = EXIF.getTag(this, "GPSLongitude");
          const latRef = EXIF.getTag(this, "GPSLatitudeRef");
          const lonRef = EXIF.getTag(this, "GPSLongitudeRef");

          if (lat && lon && latRef && lonRef) {
            const latitude = convertDMSToDD(lat[0], lat[1], lat[2], latRef);
            const longitude = convertDMSToDD(lon[0], lon[1], lon[2], lonRef);
            resolve({ latitude, longitude });
          } else {
            // No EXIF GPS data -> fallback
            const browserLocation = await getBrowserLocation(); // Await the promise
            resolve(browserLocation);
          }
        });
      }
    });
  };

  const handlePhotoUpload = async (e) => {
    setIsUploading(true);
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    // Process each file to extract metadata
    const processedPhotos = await Promise.all(files.map(async (file) => {
      // Get location data from the photo if available
      const locationData = await getPhotoLocation(file);
      console.log("browser location", locationData);
      // Create location string based on extracted coordinates
      let locationString;
      if (locationData.latitude !== null && locationData.longitude !== null) {
        // Format coordinates to 6 decimal places for consistency
        const lat = parseFloat(locationData.latitude).toFixed(6);
        const lon = parseFloat(locationData.longitude).toFixed(6);
        locationString = `${lat}° N, ${lon}° E`; // Format for display
      } else {
        // Only use default message if no coordinates were found
        locationString = "Aucune donnée de localisation disponible";
      }
      // create the blob URL once and keep it while the photo exists
      let objectUrl;
      if (file.name.toLowerCase().endsWith(".heic")) {
        objectUrl = await convertHeic(file);
      } else {
        objectUrl = URL.createObjectURL(file);
      }

      return {
        file: objectUrl,
        url: objectUrl,
        name: file.name,
        location: locationString,
        latitude: locationData.latitude, // Store raw latitude
        longitude: locationData.longitude, // Store raw longitude
        isNew: true,
        // Store EXIF data for HEIC files if needed
        exifData: file.name.toLowerCase().endsWith('.heic') ? {
          latitude: locationData.latitude,
          longitude: locationData.longitude
        } : null
      };
    }));

    setFormData((prev) => ({
      ...prev,
      photos: [...(prev.photos || []), ...processedPhotos],
    }));

    // Show success message with count of photos that have location data
    const photosWithLocation = processedPhotos.filter(p => p.latitude !== null && p.longitude !== null).length;
    if (photosWithLocation > 0) {
      // toast.success(`Ajout de ${processedPhotos.length} photo(s). Données de localisation extraites de ${photosWithLocation} photo(s).`);
    } else {
      // toast.info(`${processedPhotos.length} photo(s) ajoutée(s). Aucune donnée de localisation n'a pu être extraite.`);
    }
    setIsUploading(false);
  };

  const handleDeletePhoto = (index) => {
    setFormData(prev => {
      const photos = [...prev.photos];
      const [removed] = photos.splice(index, 1);
      if (removed?.url?.startsWith('blob:')) {
        URL.revokeObjectURL(removed.url);
      }
      return { ...prev, photos };
    });
  };


  const handleOpenModal = (index) => {
    setSelectedPhotoIndex(index);
    if (formData && formData.photos && formData.photos[index]) {
      setEditedMetadata({
        name: formData.photos[index].name || "",
        location: formData.photos[index].location || "",
      });
    } else {
      setEditedMetadata({ name: "", location: "" });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedPhotoIndex(null);
  };

  const handleMetadataChange = (e) => {
    const { name, value } = e.target;
    setEditedMetadata((prev) => ({ ...prev, [name]: value }));
  };

  const handleReplacePhoto = async (e, index) => {
    const file = e.target.files[0];
    if (!file) return;

    // Get location data
    const locationData = await getPhotoLocation(file);
    let locationString;
    if (locationData.latitude !== null && locationData.longitude !== null) {
      locationString = `${locationData.latitude.toFixed(6)}° N, ${locationData.longitude.toFixed(6)}° E`; // Format for display
    } else {
      locationString = "Aucune donnée de localisation disponible";
    }

    // Handle file conversion if needed
    let objectUrl;
    if (file.name.toLowerCase().endsWith(".heic")) {
      objectUrl = await convertHeic(file);
    } else {
      objectUrl = URL.createObjectURL(file);
    }

    // Create a new photo object with the file, URL and location data
    const updatedPhoto = {
      ...formData.photos[index],
      file: objectUrl,
      url: objectUrl,
      location: locationString,
      latitude: locationData.latitude,
      longitude: locationData.longitude,
      isNew: !formData.photos[index].id, // Correctly set isNew based on existing photo ID
      // Store EXIF data for HEIC files if needed
      exifData: file.name.toLowerCase().endsWith('.heic') ? {
        latitude: locationData.latitude,
        longitude: locationData.longitude
      } : null
    };

    // Update the photos array with the new photo
    setFormData((prev) => ({
      ...prev,
      photos: prev.photos.map((photo, i) => (i === index ? updatedPhoto : photo)),
    }));

    // Update the edited metadata to show the new location in the modal
    if (selectedPhotoIndex === index) {
      setEditedMetadata(prev => ({
        ...prev,
        location: locationString // Use the extracted/updated locationString
      }));
    }
  };

  const handleSaveMetadata = () => {
    if (selectedPhotoIndex === null) return;
    setFormData((prev) => ({
      ...prev,
      photos: prev.photos.map((photo, i) => {
        if (i === selectedPhotoIndex) {
          return { ...photo, name: editedMetadata.name, location: editedMetadata.location };
        }
        return photo;
      }),
    }));
    handleCloseModal();
    // toast.success("Les métadonnées de la photo ont été mises à jour avec succès");
  };

  const getCurrentPhotos = () => {
    return formData?.photos || [];
  };

  const handleSavePoint = async () => {
    if (showCameraConfig) return;

    // Perform validation before proceeding
    if (!validateForm(formData.pointType, formData)) {
      return; // Stop submission if validation fails
    }

    if (formData.hasCameras === "yes") { // Uses formData from the hook
      setShowCameraConfig(true);
      return;
    }
    setSaving(true);
    // Pass formData and initialCameraIds from the hook
    const success = await savePointDataApi(formData, quotationId, initialCameraIds);
    if (success) {
      console.log("Point data saved successfully");
      if (isModal) {
        onClose();
      } else {
         navigate(`/invoice/create/step3/${quotationId}`);
      }
    }
    setSaving(false);
  };

  const deleteCamera = async (cameraId) => {
    const success = await deleteCameraApi(quotationId, pointToEdit.id, cameraId);
    if (success) {
      // Update local state using setters from the hook
      setFormData(prev => ({
        ...prev,
        cameras: prev.cameras.filter(c => c.id !== cameraId)
      }));
      setInitialCameraIds(prev => prev.filter(id => id !== cameraId));
    }
  };

  const handleCameraSave = async (updatedCameras) => {
    // Use formData and setFormData from the hook
    const updatedFormData = { ...formData, cameras: updatedCameras };
    setFormData(updatedFormData);
    setShowCameraConfig(false);
    setSaving(true);
    // Pass updatedFormData and initialCameraIds from the hook
    const success = await savePointDataApi(updatedFormData, quotationId, initialCameraIds);
    if (success) {
      console.log("Point data saved successfully" , isModal);
      if (isModal) {
        onClose();
      } else {
        navigate(`/invoice/create/step3/${quotationId}`);
      }
    }
    setSaving(false);
  };

  // Render the correct form based on point type, passing the config
  const renderFormBasedOnType = () => {
    if (isLoadingConfig) { // Use isLoadingConfig from hook
      return (
        <>
          <Skeleton height={50} count={1} style={{ marginBottom: "1rem" }} />
          <Skeleton height={100} count={1} style={{ marginBottom: "1rem" }} />
          <Skeleton height={100} count={1} style={{ marginBottom: "1rem" }} />
          <Skeleton height={50} count={5} style={{ marginBottom: '1rem' }} />
        </>
      );
    }
    if (configError) { // Use configError from hook
      return (
        <p className="text-danger">
          Error loading form configuration: {configError}
        </p>
      );
    }
    if (!formData || !formData.pointType) {
      return <p>Loading form data...</p>;
    }

    const config = pointConfigurations[formData.pointType]; // Use pointConfigurations from hook

    if (!config) {
      console.warn(
        `No configuration found for point type: ${formData.pointType}`
      );
      // Optionally render a message or default form
    }

    switch (formData.pointType) {
      case "vision_point":
        return (
          <TypeOneForm
            config={config} // Pass config from hook
            type1Data={formData}
            handleChange={handleChange}
            fileInputRef={fileInputRef}
            handlePhotoUpload={handlePhotoUpload}
            showMenu={showMenu}
            setShowMenu={setShowMenu}
            handleOpenModal={handleOpenModal}
            handleDeletePhoto={handleDeletePhoto}
            isUploading={isUploading}
          />
        );
      case "relay_point":
        return (
          <TypeTwoForm
            config={config} // Pass config from hook
            type2Data={formData}
            handleChange={handleChange}
            fileInputRef={fileInputRef}
            handlePhotoUpload={handlePhotoUpload}
            showMenu={showMenu}
            setShowMenu={setShowMenu}
            handleOpenModal={handleOpenModal}
            handleDeletePhoto={handleDeletePhoto}
            isUploading={isUploading}
          />
        );
      case "high_point":
        return (
          <TypeThreeForm
            config={config} // Pass config from hook
            type3Data={formData}
            handleChange={handleChange}
            fileInputRef={fileInputRef}
            handlePhotoUpload={handlePhotoUpload}
            showMenu={showMenu}
            setShowMenu={setShowMenu}
            handleOpenModal={handleOpenModal}
            handleDeletePhoto={handleDeletePhoto}
            isUploading={isUploading}
          />
        );
      case "surveillance_center":
        return (
          <TypeFourForm
            config={config} // Pass config from hook
            type4Data={formData}
            handleChange={handleChange}
            fileInputRef={fileInputRef}
            handlePhotoUpload={handlePhotoUpload}
            showMenu={showMenu}
            setShowMenu={setShowMenu}
            handleOpenModal={handleOpenModal}
            handleDeletePhoto={handleDeletePhoto}
            isUploading={isUploading}
          />
        );
      default:
        return (
          <p>Unknown point type ({formData.pointType}) or form not loaded.</p>
        );
    }
  };

  if (!formData) {
    // Replace the main Spinner with a Skeleton loader
    return (
      <Container className="pt-4 d-flex justify-content-center">
        <>
          {" "}
          {/* Wrap Skeleton for better layout control */}
          <Skeleton height={40} style={{ marginBottom: "1rem" }} />
          <Skeleton height={200} count={2} style={{ marginBottom: "1rem" }} />
          <Skeleton height={50} />
        </>
      </Container>
    );
  }

  return (
    <HelmetProvider>
      <ToastContainer />
      <Helmet>
        <title>Edit Point - ProtectLease</title>
        <meta name="description" content="Edit point details" />
      </Helmet>

      <Container className="pt-4 d-flex justify-content-center">
        <div className="container-wrap w-100">
          <div className="header mb-4 d-flex align-items-center">
            <BackButton className="back-button" onClick={onClose} />
            <span className="step-title">Modifier le point</span>
          </div>

          <Form onSubmit={(e) => e.preventDefault()} style={{ marginBottom: "100px" }}>
            {showCameraConfig ? (
              <CameraConfig
                initialCameras={formData.cameras || []}
                onSave={handleCameraSave} // Pass the new handler
                deleteCamera={deleteCamera} // Pass the deleteCamera function
              />
            ) : (
              <>
                {renderFormBasedOnType()} {/* Render the dynamic form */}
                <div className="footer shadow-lg d-flex justify-content-center">
                  <button
                   type="button" 
                    className="w-100 primary-button primary-button-block"
                    onClick={handleSavePoint} // This button now triggers camera config if needed, or saves directly
                    disabled={saving || isLoadingConfig} // Disable while saving or loading config
                  >
                    {saving ? (
                      <>
                        <Spinner // Keep the Spinner for the button saving state
                          as="span"
                          animation="border"
                          size="sm"
                          role="status"
                          aria-hidden="true"
                        /> {" "}
                        Sauvegarde...
                      </>
                    ) : (
                      "Sauvegarder les modifications"
                    )}
                  </button>
                </div>
              </>
            )}
          </Form>
        </div>
      </Container>

      {/* Use the new PhotoMetadataModal component */}
      <PhotoMetadataModal
        show={showModal}
        handleClose={handleCloseModal}
        handleConfirm={handleSaveMetadata}
        selectedPhotoIndex={selectedPhotoIndex}
        getCurrentPhotos={getCurrentPhotos}
        handleReplacePhoto={handleReplacePhoto}
        editedMetadata={editedMetadata}
        handleMetadataChange={handleMetadataChange}
      />

      {saving && (
        <div className="full-screen-blur">
          <p style={{ color: '#333', fontSize: '1.2rem', fontWeight: 'bold' }}>Sauvegarde en cours...</p>
        </div>
      )}
    </HelmetProvider>
  );
};

export default EditPoint;